import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosHeaders,
  InternalAxiosRequestConfig,
} from "axios";

const baseURL = process.env.REACT_APP_API_END_POINT;
// const baseURL = "http://127.0.0.1:8000";

class HttpService {
  private api: AxiosInstance;
  private showAlertCallback?: (config: any) => void;

  constructor() {
    this.api = axios.create({
      baseURL,
      timeout: 30000,
    });
    this.setupInterceptors();
  }

  private isRefreshing = false;
  private failedQueue: {
    resolve: (value?: unknown) => void;
    reject: (reason?: any) => void;
    config: AxiosRequestConfig;
  }[] = [];

  private processQueue(error: any, token: string | null = null) {
    this.failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error);
      } else {
        prom.config.headers = prom.config.headers || {};
        prom.config.headers["Authorization"] = `Bearer ${token}`;
        prom.resolve(this.api(prom.config));
      }
    });
    this.failedQueue = [];
  }

  private setupInterceptors(): void {
    this.api.interceptors.request.use(
      (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
        this.applyHeaders(config);
        return config;
      },
      (error) => Promise.reject(error),
    );

    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        const status = error.response?.status;

        if (status === 401 && !originalRequest._retry) {
          if (this.isRefreshing) {
            return new Promise((resolve, reject) => {
              this.failedQueue.push({
                resolve,
                reject,
                config: originalRequest,
              });
            });
          }

          originalRequest._retry = true;
          this.isRefreshing = true;

          try {
            const emailAddress = localStorage.getItem("iqadmin_email");
            const entityPassword = localStorage.getItem("iqadmin_password");

            if (!emailAddress || !entityPassword) {
              throw new Error("No credentials available for refresh");
            }

            // Direct axios call to avoid circular dependency with AuthClient
            const refreshConfig: AxiosRequestConfig = {
              method: "POST",
              url: "/token",
              data: {
                email_address: emailAddress,
                entity_password: entityPassword,
                entity_type: "INTERVIEWIQ_ADMIN",
              },
            };
            this.applyHeaders(refreshConfig as InternalAxiosRequestConfig);
            const response = await this.api.request(refreshConfig);

            if (response.data && response.data.access_token) {
              const newToken = response.data.access_token;
              localStorage.setItem("iqadmin_token", newToken);

              if (originalRequest.headers) {
                originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
              }

              this.processQueue(null, newToken);
              return this.api(originalRequest);
            } else {
              throw new Error("Invalid token response");
            }
          } catch (refreshError) {
            this.processQueue(refreshError, null);
            this.handleSessionExpired();
            return Promise.reject(refreshError);
          } finally {
            this.isRefreshing = false;
          }
        }

        return Promise.reject(error);
      },
    );
  }

  public setShowAlert(callback: (config: any) => void): void {
    this.showAlertCallback = callback;
  }

  private handleSessionExpired(): void {
    if (this.showAlertCallback) {
      this.showAlertCallback({
        show: true,
        title: "Session Expired",
        message: "Your session has expired. Please login again to continue.",
        type: "warning",
        primaryButtonText: "Login Again",
        onPrimaryClick: () => {
          localStorage.removeItem("iqadmin_token");
          localStorage.removeItem("iqadmin_region");
          localStorage.removeItem("iqadmin_email");
          localStorage.removeItem("iqadmin_user");
          localStorage.removeItem("iqadmin_password");
          window.location.href = "/";
        },
      });
    } else {
      // Fallback if callback isn't set
      // localStorage.removeItem("iqadmin_token");
      // localStorage.removeItem("iqadmin_region");
      // localStorage.removeItem("iqadmin_email");
      // localStorage.removeItem("iqadmin_user");
      // localStorage.removeItem("iqadmin_password");
      // window.location.href = "/";
    }
  }

  private applyHeaders(
    config: InternalAxiosRequestConfig | AxiosRequestConfig,
  ): void {
    if (!config.headers) {
      config.headers = new AxiosHeaders();
    }

    // const region = localStorage.getItem("iqadmin_region");
    let apiKey: string | undefined;
    // switch (region) {
    //   case "ap-south-1":
    //     apiKey = process.env.REACT_APP_API_SERVICE_KEY_SOUTH;
    //     break;
    //   case "us-east-1":
    //     apiKey = process.env.REACT_APP_API_SERVICE_KEY_US_EAST;
    //     break;
    //   case "ap-southeast-4":
    //     apiKey = process.env.REACT_APP_API_SERVICE_KEY_SOUTH_EAST;
    //     break;
    //   default:
    //     apiKey = process.env.REACT_APP_API_SERVICE_KEY_SOUTH;
    // }
    apiKey = process.env.REACT_APP_API_SERVICE_KEY;
    if (apiKey) {
      (config.headers as AxiosHeaders).set("x-api-key", apiKey);
    }

    const token = localStorage.getItem("iqadmin_token");
    if (token && config.url !== "/token") {
      (config.headers as AxiosHeaders).set("Authorization", `Bearer ${token}`);
    }

    if (config.data && !(config.headers as AxiosHeaders).get("Content-Type")) {
      (config.headers as AxiosHeaders).set("Content-Type", "application/json");
    }
  }

  get<T = any>(url: string, config?: AxiosRequestConfig): Promise<{ data: T }> {
    return this.api.get(url, config);
  }

  post<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<{ data: T }> {
    return this.api.post(url, data, config);
  }

  put<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<{ data: T }> {
    return this.api.put(url, data, config);
  }

  delete<T = any>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<{ data: T }> {
    return this.api.delete(url, config);
  }
}

const httpService = new HttpService();
export default httpService;
